import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { FlexContainer, Icon, ModalContext, Modals, Text } from '@taxdown/piccolo-web';
import { YayoyakiFormatter } from '@taxdown/kami-sama/dist/kamiSama/yayoyakiFormatter';
import { locizeNS } from 'Services/i18n';
import { userStageSelector } from 'Store/modules/validations/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { useFetchPreviousYearCacheInfo } from 'Containers/Dashboard/hooks';
import ValidationCompensation from 'Components/Modal/components/ValidationCompensation';
import { stages } from 'Constants/stages';
import { ResultTypes } from 'Types/common';
import * as Styled from './styles';
import { ResultDetailCompensationProps } from './types';

const ZERO_AMOUNT = 0;

const ResultDetailCompensation = ({
  taxReturnResult,
  filingInfo,
}: ResultDetailCompensationProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.DASHBOARD);
  const { showModal, hideModal } = useContext(ModalContext);
  const userStage = useSelector(userStageSelector);
  const data = useFetchPreviousYearCacheInfo();

  const daResultValue =
    filingInfo?.recent?.amount ||
    data?.compensatedTaxReturns?.[0]?.amount ||
    taxReturnResult?.taxReturnResult;

  const amountTotal = Math.abs(Number(data?.amountTotal));
  const amountResult = Math.abs(Number(daResultValue));

  const initialAmount = amountTotal - amountResult;

  const maiorAA = amountTotal > amountResult;

  const equalAA = amountTotal === amountResult;

  const toPayWithTaxDown = maiorAA || equalAA ? ZERO_AMOUNT : initialAmount;

  const priceMap = {
    [stages.VALIDATION]: data?.compensatedTaxReturns?.[0]?.amount,
    default: filingInfo?.recent?.amount || taxReturnResult?.taxReturnResult,
  };
  const priceToPay = userStage === stages.VALIDATION ? priceMap[userStage] : priceMap.default;

  const handleClick = (): void => {
    if (userStage === stages.VALIDATION) {
      showModal({
        comp: (
          <ValidationCompensation
            initial={taxReturnResult?.taxReturnResult}
            newAmount={filingInfo?.recent?.amount}
            previousYears={data?.amountTotal}
            total={toPayWithTaxDown}
          />
        ),
      });
    } else {
      showModal({
        comp: (
          <Modals.Centered
            maxSize="S"
            buttonsInline
            hasClose
            onClose={hideModal}
            title={t('Compensation.modal.title')}
            description={t('Compensation.modal.description')}
            primaryButton={{
              label: t('Compensation.modal.button'),
              onClick: hideModal,
            }}
          />
        ),
      });
    }
  };
  return (
    <Styled.ResultInfoContainer direction="column" align="center" gap="dimension_08">
      <FlexContainer direction="column" align="center">
        <FlexContainer direction="column" gap="dimension_08" align="center">
          <Text type="paragraphs" color="grey" size="XS_M_S" align="center">
            {t('Amount.toPay.sat', {
              context: userStage === stages.VALIDATION && 'validation',
              price: YayoyakiFormatter.formatAmountMxn(Math.abs(Number(priceToPay))),
              newPrice: YayoyakiFormatter.formatAmountMxn(
                Math.abs(Number(filingInfo?.recent?.amount))
              ),
            })}
          </Text>
          <FlexContainer align="center" gap="dimension_08">
            <Styled.AmountResolved
              type="paragraphs"
              size="S_M_S"
              $resultType={ResultTypes.toPay}
              $stage={userStage}
            >
              {t(`AmountResolved.compensation.toPay`)}
            </Styled.AmountResolved>
            <Styled.Button type="button" onClick={handleClick}>
              <Icon icon="InfoXS" color="cold" />
            </Styled.Button>
          </FlexContainer>
        </FlexContainer>
        <Styled.AmountResolved
          type="paragraphs"
          size="L_M_S"
          $resultType={ResultTypes.toPay}
          $stage={userStage}
        >
          {YayoyakiFormatter.formatAmountMxn(Math.abs(toPayWithTaxDown))}
        </Styled.AmountResolved>
      </FlexContainer>
    </Styled.ResultInfoContainer>
  );
};

export default ResultDetailCompensation;
